import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Search, Loader2, Link2, ScanLine, History, CheckCircle2, AlertTriangle, ArrowRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, describeError } from "@/lib/api";
import { availityPayers, curatedPayers, patchInsurance, pgPatientHistory, searchNotes, getNote } from "@/lib/noteClaim";
import InsuranceCardsStrip from "@/components/claim/InsuranceCardsStrip";

const HAIR = "#E9E9EC", INK = "#141412", GREY = "#6E6E6B", SUB = "#6C6C69", INDIGO = "#5856D6";
const MONO = "ui-monospace,Menlo,monospace";

const fileToB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });

// A payer needs an id when a carrier is known but the Availity id is missing/unresolved.
function needsId(ins) {
  const p = ins || {};
  if (!p.payer_name) return false;
  return !!(p.needs_review || p.payer_id_missing || !p.payer_id || p.payer_id === "MISSING");
}
const idText = (p) => (p?.payer_id && p.payer_id !== "MISSING" ? p.payer_id : "—");

// Live Availity typeahead → pick a payer_id + name. Falls back to the curated list
// (which has ids) when the Availity master returns nothing in this environment.
export function PayerPicker({ seq, clientId, onPick }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [curated, setCurated] = useState([]);
  const tRef = useRef(null);
  useEffect(() => { let a = true; curatedPayers(clientId).then((p) => a && setCurated(p)).catch(() => {}); return () => { a = false; }; }, [clientId]);
  useEffect(() => {
    if (!q || q.trim().length < 2) { setRows(null); return undefined; }
    setLoading(true);
    clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      try {
        let r = await availityPayers(q, 20);
        if (!r.length) { const s = q.trim().toLowerCase(); r = curated.filter((p) => p.name.toLowerCase().includes(s) || String(p.payer_id).includes(s)).slice(0, 20); }
        setRows(r);
      } catch { setRows([]); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(tRef.current);
  }, [q, curated]);
  return (
    <div>
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: GREY }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Availity payers by name or id…"
          className="w-full text-[12.5px] border rounded-[9px] pl-8 pr-3 py-2 outline-none bg-white" style={{ borderColor: HAIR }}
          data-testid={`mi-payer-search-${seq}`} />
      </div>
      {q.trim().length >= 2 ? (
        <div className="mt-1.5 max-h-[160px] overflow-y-auto rounded-[9px] border" style={{ borderColor: HAIR }}>
          {loading ? (
            <div className="text-[12px] px-3 py-2 inline-flex items-center gap-1.5" style={{ color: GREY }}><Loader2 size={12} className="animate-spin" />Searching…</div>
          ) : rows && rows.length ? rows.map((r) => (
            <button key={r.payer_id} onClick={() => { onPick({ payer_id: r.payer_id, payer_name: r.name }); setQ(""); setRows(null); }}
              className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-black/[.04]" data-testid={`mi-payer-opt-${seq}-${r.payer_id}`}>
              <span className="text-[12.5px] font-semibold flex-1 min-w-0 truncate" style={{ color: INK }}>{r.name}</span>
              <span className="text-[11px]" style={{ color: SUB, fontFamily: MONO }}>{r.payer_id}</span>
            </button>
          )) : (
            <div className="text-[12px] px-3 py-2" style={{ color: GREY }}>No payer match for “{q}”.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Primary (seq 1) or Secondary (seq 2) editor.
function PayerEditor({ seq, current, onFile, clientId, busy, onSave }) {
  const [picked, setPicked] = useState(null); // { payer_id, payer_name }
  const [member, setMember] = useState(current?.member_id || "");
  const [group, setGroup] = useState(current?.group_number || "");
  const [expand, setExpand] = useState(seq === 1 || !!current?.payer_name);
  useEffect(() => { setMember(current?.member_id || ""); setGroup(current?.group_number || ""); }, [current?.member_id, current?.group_number]);

  const title = seq === 1 ? "Primary payer" : "Secondary payer";
  const has = !!current?.payer_name;
  const chip = needsId(current);
  const effName = picked?.payer_name || current?.payer_name;
  const effId = picked?.payer_id || (current?.payer_id && current.payer_id !== "MISSING" ? current.payer_id : "");

  const save = () => {
    const payer_id = picked?.payer_id || (current?.payer_id && current.payer_id !== "MISSING" ? current.payer_id : "");
    if (!payer_id && !picked) {
      // only member/group change
      onSave({ payer_id: "", member_id: member || undefined, group_number: group || undefined, sequence: seq });
      return;
    }
    if (!payer_id) { toast.error("Pick a payer from the Availity list (a real payer id is required)."); return; }
    onSave({ payer_id, payer_name: picked?.payer_name || current?.payer_name, member_id: member || undefined, group_number: group || undefined, sequence: seq });
  };

  if (seq === 2 && !has && !expand) {
    return (
      <button onClick={() => setExpand(true)} className="w-full text-left rounded-[12px] border border-dashed px-3.5 py-3 text-[12.5px] font-semibold inline-flex items-center gap-2" style={{ borderColor: HAIR, color: INDIGO }} data-testid="mi-add-secondary">
        <Link2 size={14} /> Add a secondary payer
      </button>
    );
  }

  return (
    <div className="rounded-[12px] border bg-white p-3.5" style={{ borderColor: HAIR }} data-testid={`mi-editor-${seq}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10.5px] font-bold uppercase tracking-[.05em]" style={{ color: GREY }}>{title}</span>
        {has ? (chip
          ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FBE9E7] text-[#C22219]" data-testid={`mi-needsid-${seq}`}><AlertTriangle size={10} />needs payer id</span>
          : <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E7F6EE] text-[#1F9D57]"><CheckCircle2 size={10} />resolved</span>
        ) : <span className="text-[11px]" style={{ color: GREY }}>none on the claim</span>}
      </div>

      {has ? (
        <div className="text-[12.5px] mb-2.5" style={{ color: INK }} data-testid={`mi-current-${seq}`}>
          <b>{current.payer_name}</b> <span style={{ color: SUB, fontFamily: MONO }}>· id {idText(current)}</span>
          {current.member_id ? <span style={{ color: SUB }}> · member {current.member_id}</span> : null}
        </div>
      ) : null}

      {onFile && onFile.payer_name && (onFile.payer_id || onFile.member_id) && onFile.payer_id !== current?.payer_id ? (
        <div className="flex items-center gap-2 text-[11.5px] rounded-[9px] px-2.5 py-2 mb-2.5" style={{ background: "#F7F7FB", color: SUB }} data-testid={`mi-onfile-${seq}`}>
          <History size={13} /> On file: <b style={{ color: INK }}>{onFile.payer_name}</b>{onFile.payer_id && onFile.payer_id !== "MISSING" ? ` · ${onFile.payer_id}` : ""}
          <span className="flex-1" />
          <button disabled={busy} onClick={() => onSave({ payer_id: onFile.payer_id, payer_name: onFile.payer_name, member_id: onFile.member_id || undefined, sequence: seq })}
            className="text-[11px] font-bold" style={{ color: INDIGO }} data-testid={`mi-useonfile-${seq}`}>Use this</button>
        </div>
      ) : null}

      <PayerPicker seq={seq} clientId={clientId} onPick={setPicked} />
      {picked ? <div className="text-[11.5px] mt-1.5" style={{ color: INDIGO }} data-testid={`mi-picked-${seq}`}>Selected: <b>{picked.payer_name}</b> · {picked.payer_id}</div> : null}

      <div className="grid grid-cols-2 gap-2 mt-2.5">
        <div>
          <label className="text-[10.5px] font-semibold" style={{ color: GREY }}>Member ID</label>
          <input value={member} onChange={(e) => setMember(e.target.value)} className="w-full mt-1 text-[12.5px] border rounded-[8px] px-2.5 py-1.5 bg-white" style={{ borderColor: HAIR }} data-testid={`mi-member-${seq}`} />
        </div>
        <div>
          <label className="text-[10.5px] font-semibold" style={{ color: GREY }}>Group</label>
          <input value={group} onChange={(e) => setGroup(e.target.value)} className="w-full mt-1 text-[12.5px] border rounded-[8px] px-2.5 py-1.5 bg-white" style={{ borderColor: HAIR }} data-testid={`mi-group-${seq}`} />
        </div>
      </div>

      <div className="flex items-center justify-end mt-3">
        <button onClick={save} disabled={busy} className="text-[12px] font-bold text-white rounded-[8px] px-3.5 py-2 inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: INK }} data-testid={`mi-save-${seq}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}{effName ? `Save ${seq === 1 ? "primary" : "secondary"}` : "Save"}
        </button>
      </div>
    </div>
  );
}

function money(v) { const n = Number(v); return isFinite(n) ? `$${n.toFixed(2)}` : "—"; }

// Live EHI claim: header with claim-level info (Payer ID + Policy No highlighted) that drills
// down to the service lines. renderInherit draws the →Primary/→Secondary buttons for this claim.
const EHI_COLS = "50px 48px 62px 40px 66px 70px 66px 66px 60px 62px";
function EhiClaimCard({ c, renderInherit, highlight, defaultOpen, showPayer = true }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const lines = c.lines || [];
  const chip = { fontFamily: MONO, fontWeight: 700, background: "#EEF0FF", color: INDIGO };
  return (
    <div className="rounded-[10px] border bg-white overflow-hidden" style={{ borderColor: highlight ? INDIGO : HAIR, boxShadow: highlight ? `0 0 0 2px ${INDIGO}22` : "none" }} data-testid={`mi-ehi-${c.encno}`} data-closest={highlight ? "1" : undefined}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-[#FAFAFB]" data-testid={`mi-ehi-toggle-${c.encno}`} aria-expanded={open}>
        <ChevronDown size={14} style={{ color: GREY, flex: "none", transform: open ? "none" : "rotate(-90deg)", transition: "transform .18s" }} />
        <span className="text-[12.5px] font-semibold" style={{ color: INK }}>{c.dos || "—"}</span>
        <span className="text-[10px] rounded-[5px] px-1.5 py-0.5 flex-none" style={{ background: "#F1F1F4", color: SUB, fontFamily: MONO }}>Enc {c.encno || "—"}</span>
        <span className="flex-1" />
        <span className="text-[11px]" style={{ color: GREY }}>{lines.length} line{lines.length === 1 ? "" : "s"}</span>
        <span className="text-[12px] font-semibold tabular-nums" style={{ color: INK }}>{money(c.total_billed)}</span>
      </button>
      <div className="flex items-center gap-1.5 flex-wrap px-2.5 pb-2" style={{ paddingLeft: 30 }}>
        {showPayer ? <span className="inline-flex items-center text-[10.5px] rounded-[6px] px-1.5 py-0.5" style={chip} title="Payer ID" data-testid={`mi-ehi-payer-${c.encno}`}>Payer {c.payer_id || "—"}</span> : null}
        {showPayer ? <span className="inline-flex items-center text-[10.5px] rounded-[6px] px-1.5 py-0.5" style={chip} title="Policy / member no" data-testid={`mi-ehi-policy-${c.encno}`}>Policy {c.policy_no || "—"}</span> : null}
        <span className="text-[10px]" style={{ color: GREY, fontFamily: MONO }}>Chart {c.chart_id || "—"} · NPI {c.npi || "—"}</span>
        {highlight ? <span className="inline-flex items-center text-[10px] font-bold rounded-[6px] px-1.5 py-0.5" style={{ background: INDIGO, color: "#fff" }} data-testid={`mi-ehi-closest-${c.encno}`}>Closest to this visit</span> : null}
        <span className="flex-1" />
        {renderInherit ? renderInherit(c) : null}
      </div>
      {open ? (
        <div className="border-t overflow-x-auto" style={{ borderColor: HAIR, background: "#FCFCFD" }} data-testid={`mi-ehi-lines-${c.encno}`}>
          <div style={{ minWidth: 620 }}>
            <div className="grid gap-1 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-[.03em]" style={{ gridTemplateColumns: EHI_COLS, color: GREY }}>
              <span>CPT</span><span>Mods</span><span>Dx</span><span className="text-right">Units</span><span className="text-right">Billed</span><span className="text-right">Ins paid</span><span className="text-right">Pt paid</span><span className="text-right">Adjust</span><span className="text-right">2nd bal</span><span className="text-right">Pt bal</span>
            </div>
            {lines.map((ln, i) => (
              <div key={i} data-testid={`mi-ehi-line-${c.encno}-${i}`}>
                <div className="grid gap-1 px-2.5 py-1.5 text-[11px] tabular-nums items-center" style={{ gridTemplateColumns: EHI_COLS, color: INK, borderTop: `1px solid ${HAIR}` }}>
                  <span className="font-semibold" style={{ fontFamily: MONO }}>{ln.cpt || "—"}</span>
                  <span style={{ color: SUB }}>{(ln.mods || []).join(", ") || "—"}</span>
                  <span style={{ color: SUB }}>{(ln.dx || []).join(", ") || "—"}</span>
                  <span className="text-right">{ln.units ?? "—"}</span>
                  <span className="text-right">{money(ln.billed)}</span>
                  <span className="text-right" style={{ color: "#1F7A4D" }}>{money(ln.ins_payment)}</span>
                  <span className="text-right">{money(ln.patient_payment)}</span>
                  <span className="text-right" style={{ color: GREY }}>{money(ln.adjustment)}</span>
                  <span className="text-right">{money(ln.secondary_bal)}</span>
                  <span className="text-right" style={{ color: Number(ln.patient_balance) > 0 ? "#B3261E" : INK }}>{money(ln.patient_balance)}</span>
                </div>
                {ln.ar_status ? <div className="px-2.5 pb-1.5 text-[10px]" style={{ color: GREY, paddingLeft: 10 }} data-testid={`mi-ehi-ar-${c.encno}-${i}`}>AR · {ln.ar_status}</div> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function HistoryPanel({ clientId, mrn, excludeId, onInherit, busy, visitDos }) {
  const [visits, setVisits] = useState(null);
  const [ehi, setEhi] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [ehiView, setEhiView] = useState("payer");
  useEffect(() => {
    let alive = true;
    if (!mrn) { setVisits([]); setEhi({ bound: false, claims: [] }); return undefined; }
    searchNotes({ client_id: String(clientId), mrn, limit: 50 }).then((r) => alive && setVisits((r.notes || []).filter((n) => n.id !== excludeId))).catch(() => alive && setVisits([]));
    pgPatientHistory(clientId, mrn).then((d) => alive && setEhi(d)).catch(() => alive && setEhi({ bound: false, claims: [] }));
    return () => { alive = false; };
  }, [clientId, mrn, excludeId]);

  const claims = ehi?.claims || [];
  const flatClaims = useMemo(() => [...claims].sort((a, b) => (Date.parse(b.dos) || 0) - (Date.parse(a.dos) || 0)), [claims]);
  // The claim whose DOS is nearest the visit being managed — highlighted in both views.
  const closestEnc = useMemo(() => {
    const vt = visitDos ? Date.parse(visitDos) : NaN;
    if (isNaN(vt) || !claims.length) return null;
    let best = null, bd = Infinity;
    for (const c of claims) { const t = Date.parse(c.dos); if (isNaN(t)) continue; const d = Math.abs(t - vt); if (d < bd) { bd = d; best = c.encno; } }
    return best;
  }, [claims, visitDos]);
  // Group by Payer + Policy; newest-DOS group first, newest claim first within a group.
  const groups = useMemo(() => {
    const m = new Map();
    for (const c of claims) { const k = `${c.payer_id || "—"}|${c.policy_no || "—"}`; if (!m.has(k)) m.set(k, { payer_id: c.payer_id, policy_no: c.policy_no, claims: [] }); m.get(k).claims.push(c); }
    const arr = [...m.values()];
    arr.forEach((g) => { g.claims.sort((a, b) => (Date.parse(b.dos) || 0) - (Date.parse(a.dos) || 0)); g.max = g.claims.reduce((mx, c) => Math.max(mx, Date.parse(c.dos) || -Infinity), -Infinity); });
    arr.sort((a, b) => b.max - a.max);
    return arr;
  }, [claims]);

  const InheritBtns = ({ payer_id, payer_name, member_id }) => {
    const preview = `${payer_name || "payer"} · id ${payer_id}${member_id ? ` · member ${member_id}` : " · no member id"}`;
    return (payer_id && payer_id !== "MISSING") ? (
      <div className="inline-flex items-center gap-1.5">
        <button disabled={busy} title={`Preview — set primary: ${preview}`} onClick={() => setConfirm({ payer_id, payer_name, member_id, sequence: 1 })} className="text-[10.5px] font-bold rounded-[6px] px-2 py-1 border bg-white" style={{ borderColor: HAIR, color: INK }} data-testid="mi-inherit-primary">→ Primary</button>
        <button disabled={busy} title={`Preview — set secondary: ${preview}`} onClick={() => setConfirm({ payer_id, payer_name, member_id, sequence: 2 })} className="text-[10.5px] font-bold rounded-[6px] px-2 py-1 border bg-white" style={{ borderColor: HAIR, color: INK }} data-testid="mi-inherit-secondary">→ Secondary</button>
      </div>
    ) : <span className="text-[10.5px]" style={{ color: GREY }}>no payer id</span>;
  };

  if (!mrn) return <div className="text-[12.5px] py-6 text-center" style={{ color: GREY }} data-testid="mi-history-nomrn">Match this visit to a patient to see their payer history.</div>;
  const renderInherit = (cl) => <InheritBtns payer_id={cl.payer_id} payer_name={cl.payer_name || ""} member_id={cl.policy_no} />;

  return (
    <div data-testid="mi-history">
      {confirm ? (
        <div className="mb-3 rounded-[11px] border p-3 sticky top-0 z-[2]" style={{ borderColor: "#C9C9F0", background: "#F4F4FD" }} data-testid="mi-inherit-confirm">
          <div className="text-[11px] font-bold uppercase tracking-[.05em] mb-1" style={{ color: INDIGO }}>Confirm inherit</div>
          <div className="text-[12.5px]" style={{ color: INK }}>
            Set as <b>{confirm.sequence === 2 ? "secondary" : "primary"}</b> payer: <b>{confirm.payer_name || "payer"}</b>
            <span style={{ color: SUB }}> · id {confirm.payer_id}{confirm.member_id ? ` · member ${confirm.member_id}` : " · no member id"}</span>
          </div>
          <div className="flex items-center justify-end gap-2 mt-2.5">
            <button onClick={() => setConfirm(null)} className="text-[11.5px] font-semibold px-2.5 py-1.5" style={{ color: GREY }} data-testid="mi-inherit-cancel">Cancel</button>
            <button disabled={busy} onClick={() => { onInherit({ payer_id: confirm.payer_id, payer_name: confirm.payer_name, member_id: confirm.member_id, sequence: confirm.sequence }); setConfirm(null); }}
              className="text-[11.5px] font-bold text-white rounded-[7px] px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-40" style={{ background: INK }} data-testid="mi-inherit-apply">
              {busy ? <Loader2 size={11} className="animate-spin" /> : null}Apply as {confirm.sequence === 2 ? "secondary" : "primary"}
            </button>
          </div>
        </div>
      ) : null}
      <div className="text-[10.5px] font-bold uppercase tracking-[.05em] mb-2" style={{ color: GREY }}>Prior Note-to-Claim visits</div>
      {visits == null ? <div className="text-[12px] inline-flex items-center gap-1.5 mb-3" style={{ color: GREY }}><Loader2 size={12} className="animate-spin" />Loading…</div>
        : !visits.length ? <div className="text-[12px] mb-3" style={{ color: GREY }}>No prior visits for this patient.</div>
        : (
          <div className="flex flex-col gap-2 mb-4">
            {visits.slice(0, 12).map((v) => (
              <div key={v.id} className="rounded-[10px] border bg-white p-2.5" style={{ borderColor: HAIR }} data-testid={`mi-visit-${v.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[12px] font-semibold" style={{ color: INK }}>{v.date_of_service || "no DOS"}</span>
                  <span className="text-[11px]" style={{ color: GREY }}>{v.provider_name || ""}</span>
                  <span className="flex-1" />
                  <span className="text-[10px]" style={{ color: GREY }}>{v.claim_status || v.status}</span>
                </div>
                {[["Primary", v.insurance], ["Secondary", v.insurance_secondary]].filter(([, ins]) => ins?.payer_name).map(([lbl, ins]) => (
                  <div key={lbl} className="flex items-center gap-2 py-1">
                    <span className="text-[11.5px]" style={{ color: SUB }}><b style={{ color: INK }}>{ins.payer_name}</b>{ins.member_id ? ` · ${ins.member_id}` : ""} <span style={{ color: GREY }}>({lbl})</span></span>
                    <span className="flex-1" />
                    <InheritBtns payer_id={ins.payer_id} payer_name={ins.payer_name} member_id={ins.member_id} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

      <div className="text-[10.5px] font-bold uppercase tracking-[.05em] mb-2" style={{ color: GREY }}>Live EHI claims</div>
      {ehi == null ? <div className="text-[12px] inline-flex items-center gap-1.5" style={{ color: GREY }}><Loader2 size={12} className="animate-spin" />Loading…</div>
        : !ehi.bound ? <div className="text-[12px]" style={{ color: GREY }} data-testid="mi-ehi-unbound">No live EHI connection for this client.</div>
        : !(ehi.claims || []).length ? <div className="text-[12px]" style={{ color: GREY }}>No live claims on file.</div>
        : (
          <div data-testid="mi-ehi-list">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[11px]" style={{ color: GREY }}>{ehi.claims.length} claim{ehi.claims.length === 1 ? "" : "s"} · {ehi.line_count ?? ehi.claims.reduce((s, c) => s + (c.lines || []).length, 0)} lines</span>
              <span className="flex-1" />
              <div className="inline-flex items-center rounded-[8px] p-[2px]" style={{ background: "#F1F1F4" }} role="radiogroup" aria-label="EHI claims view" data-testid="mi-ehi-view">
                {[["payer", "By payer & policy"], ["flat", "Flat"]].map(([k, l]) => (
                  <button key={k} type="button" role="radio" aria-checked={ehiView === k} onClick={() => setEhiView(k)} data-testid={`mi-ehi-view-${k}`}
                    className="text-[11px] font-semibold rounded-[6px] px-2 py-1 transition-colors" style={ehiView === k ? { background: "#fff", color: INK, boxShadow: "0 1px 2px rgba(0,0,0,.08)" } : { background: "transparent", color: SUB }}>{l}</button>
                ))}
              </div>
            </div>
            {ehiView === "flat" ? (
              <div className="flex flex-col gap-2">
                {flatClaims.map((c, i) => <EhiClaimCard key={c.encno || i} c={c} highlight={c.encno === closestEnc} defaultOpen={c.encno === closestEnc} renderInherit={renderInherit} />)}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {groups.map((g, gi) => {
                  const hot = g.claims.some((c) => c.encno === closestEnc);
                  return (
                    <div key={gi} className="rounded-[11px] border overflow-hidden" style={{ borderColor: hot ? INDIGO : HAIR, boxShadow: hot ? `0 0 0 2px ${INDIGO}22` : "none" }} data-testid={`mi-ehi-group-${g.payer_id || "x"}-${g.policy_no || "x"}`}>
                      <div className="flex items-center gap-3 flex-wrap px-3 py-2.5" style={{ background: "#F3F3FB", borderBottom: `1px solid #E4E4F4`, borderLeft: `3px solid ${INDIGO}` }}>
                        <div className="flex flex-col leading-tight">
                          <span className="text-[13.5px] font-bold tracking-[-0.1px]" style={{ color: INK }} title="Payer ID">Payer {g.payer_id || "—"}</span>
                          <span className="text-[11px] mt-0.5" style={{ color: SUB, fontFamily: MONO }} title="Policy / member no">Policy {g.policy_no || "—"}</span>
                        </div>
                        <span className="text-[10.5px] rounded-full px-2 py-0.5" style={{ background: "#fff", color: SUB, border: `1px solid ${HAIR}` }}>{g.claims.length} claim{g.claims.length === 1 ? "" : "s"} · latest {g.claims[0]?.dos || "—"}</span>
                        <span className="flex-1" />
                        <InheritBtns payer_id={g.payer_id} payer_name="" member_id={g.policy_no} />
                      </div>
                      <div className="flex flex-col gap-2 p-2">
                        {g.claims.map((c, i) => <EhiClaimCard key={c.encno || i} c={c} showPayer={false} highlight={c.encno === closestEnc} defaultOpen={c.encno === closestEnc} renderInherit={renderInherit} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
    </div>
  );
}

// Manage Insurance — primary + secondary via Availity, on-file reference, inherit from history, card scan.
export default function ManageInsuranceModal({ note: initialNote, clientId, onClose, onUpdated, initialScan = false }) {
  const [note, setNote] = useState(initialNote);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanRef = useRef(null);
  const noteId = note?.id;
  const cid = clientId || note?.client_id;

  const refetch = useCallback(async () => {
    let merged = null;
    try {
      const n = await getNote(noteId);
      setNote((p) => { merged = n ? { ...p, ...n, insurance: n.insurance ?? p.insurance, insurance_secondary: n.insurance_secondary ?? p.insurance_secondary, insurance_on_file: n.insurance_on_file ?? p.insurance_on_file } : p; return merged; });
    } catch {}
    onUpdated?.(merged);
  }, [noteId, onUpdated]);

  const applyPatch = useCallback(async (body) => {
    setBusy(true);
    try {
      const res = await patchInsurance(noteId, body);
      if (res?.insurance) setNote((p) => ({ ...p, [body.sequence === 2 ? "insurance_secondary" : "insurance"]: res.insurance }));
      const swept = res?.visits_updated ? ` · ${res.visits_updated} other visit${res.visits_updated === 1 ? "" : "s"} updated` : "";
      toast.success(`${body.sequence === 2 ? "Secondary" : "Primary"} payer saved${res?.payer_registered ? " · added to Payers master" : ""}${swept}`);
      await refetch();
    } catch (e) { toast.error(describeError(e)); } finally { setBusy(false); }
  }, [noteId, refetch]);

  useEffect(() => { if (initialScan) scanRef.current?.click(); }, [initialScan]);
  const onScan = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setScanning(true);
    try {
      const b64 = await fileToB64(f);
      await api.post(`/api/edi/note-to-claim/notes/${noteId}/scan-insurance-card`, { file_base64: b64 });
      toast.success("Insurance card scanned");
      await refetch();
    } catch (ex) { toast.error(describeError(ex)); } finally { setScanning(false); }
  };

  useEffect(() => { const onKey = (e) => { if (e.key === "Escape") onClose?.(); }; window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey); }, [onClose]);

  const body = (
    <div style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(20,20,18,.5)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="bg-white rounded-[18px] overflow-hidden flex flex-col" style={{ width: "min(1060px,97vw)", maxHeight: "92vh", boxShadow: "0 28px 80px rgba(0,0,0,.3)" }} role="dialog" data-testid="manage-insurance-modal">
        <div className="flex items-center gap-3 px-5 py-3.5 border-b flex-none" style={{ borderColor: HAIR }}>
          <span className="w-8 h-8 rounded-[9px] grid place-items-center text-white flex-none" style={{ background: "linear-gradient(145deg,#1a1a1a,#000)" }}><Link2 size={15} /></span>
          <div className="min-w-0">
            <div className="text-[15px] font-bold" style={{ color: INK }}>Manage insurance</div>
            <div className="text-[11.5px]" style={{ color: GREY }}>{note?.patient_name || "—"}{note?.mrn ? ` · MRN ${note.mrn}` : " · no MRN"} · {note?.visit_id || noteId}</div>
          </div>
          <span className="flex-1" />
          <button onClick={() => scanRef.current?.click()} disabled={scanning} className="text-[11.5px] font-bold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5" style={{ background: "#FBF0DF", color: "#9A5A00" }} data-testid="mi-scan-btn">
            {scanning ? <Loader2 size={12} className="animate-spin" /> : <ScanLine size={12} />}Scan card
          </button>
          <input ref={scanRef} type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" className="hidden" onChange={onScan} data-testid="mi-scan-input" />
          <button onClick={onClose} className="w-8 h-8 rounded-[8px] grid place-items-center" style={{ color: GREY }} data-testid="mi-close"><X size={17} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto" style={{ background: "#F7F7F9" }}>
          <div className="px-4 pt-4">
            <InsuranceCardsStrip clientId={cid} mrn={note?.mrn} patientName={note?.patient_name} />
          </div>
          <div className="grid gap-4 p-4" style={{ gridTemplateColumns: "minmax(0,1fr) minmax(0,.95fr)" }}>
            <div className="flex flex-col gap-3">
              <PayerEditor seq={1} current={note?.insurance} onFile={note?.insurance_on_file} clientId={cid} busy={busy} onSave={applyPatch} />
              <PayerEditor seq={2} current={note?.insurance_secondary} onFile={null} clientId={cid} busy={busy} onSave={applyPatch} />
              <div className="text-[11px] leading-snug" style={{ color: GREY }}>
                On file is what the patient match produced; the saved payer is what actually bills. Linking a real Availity id also teaches the client crosswalk and sweeps other pending visits.
              </div>
            </div>
            <div className="rounded-[12px] border bg-white p-3.5" style={{ borderColor: HAIR }}>
              <HistoryPanel clientId={cid} mrn={note?.mrn} excludeId={noteId} onInherit={applyPatch} busy={busy} visitDos={note?.date_of_service} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(body, document.body);
}
